import axios from "axios";
import React, { useEffect, useState } from "react";
import { FaPaintBrush } from "react-icons/fa";
import { FaDisplay, FaLocationDot } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/pagination";
import { Keyboard, Pagination, Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

const DisplayScreen = ({ sellerId, accessToken }) => {
  const [screenshotImages, setScreenshotImages] = useState([]);
  const [screenShotLoading, setScreenShotLoading] = useState(false);

  const getDisplayScreenshot = async () => {
    try {
      setScreenShotLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/display/list/${sellerId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const displays = res.data.data || [];

      const validDisplays = displays
        .filter((display) => display && display.screenShotPath)
        .map((display) => {
          let layoutNames = [];
          if (display.layoutId) {
            try {
              const layouts = JSON.parse(display.layoutId);
              layoutNames = Array.isArray(layouts)
                ? layouts.map((layout) => layout.name)
                : [];
            } catch (e) {
              console.error("Error parsing layoutId:", e);
              layoutNames = [];
            }
          }

          return {
            ...display,
            url: display.screenShotPath,
            layoutNames: layoutNames,
          };
        });

      setScreenshotImages(validDisplays);
    } catch (err) {
      console.error("Error fetching display list:", err);
    } finally {
      setScreenShotLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && sellerId) getDisplayScreenshot();
  }, [accessToken, sellerId]);

  const checkImageExists = async (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  useEffect(() => {
    const filterValidImages = async () => {
      try {
        setScreenShotLoading(true);
        if (screenshotImages && screenshotImages.length > 0) {
          const checkPromises = screenshotImages.map(async (screenshot) => {
            const imageUrl = screenshot.url || screenshot.screenShotPath;
            if (!imageUrl) return null;

            const exists = await checkImageExists(imageUrl);
            return exists ? screenshot : null;
          });

          const checkedScreenshots = await Promise.all(checkPromises);

          const validScreenshots = checkedScreenshots.filter(Boolean);

          const grouped = validScreenshots.reduce((acc, screenshot) => {
            const location = screenshot.location || "Unassigned Location";
            if (!acc[location]) {
              acc[location] = [];
            }
            acc[location].push(screenshot);
            return acc;
          }, {});

          setGroupedScreenshots(grouped);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setScreenShotLoading(false);
      }
    };

    filterValidImages();
  }, [screenshotImages]);

  const navigate = useNavigate();
  const [groupedScreenshots, setGroupedScreenshots] = useState({});

  const handleSlideClick = (id) => {
    navigate(`/displayPreview/${id}`);
  };

  if (screenShotLoading) {
    return (
      <div>
        <h6 className="tw-mt-8 tw-font-sans tw-text-lg">Display</h6>
        <Swiper
          slidesPerView={4}
          spaceBetween={10}
          mousewheel={true}
          pagination={{
            clickable: true,
            renderBullet: function (_, className) {
              return `<span class="${className} tw-w-3 tw-h-3 tw-bg-gray-300 tw-rounded-full tw-mx-1 tw-cursor-pointer hover:tw-bg-gray-400 active:tw-bg-gray-500"></span>`;
            },
          }}
          keyboard={{
            enabled: true,
          }}
          breakpoints={{
            320: {
              slidesPerView: 1,
              spaceBetween: 10,
            },
            640: {
              slidesPerView: 2,
              spaceBetween: 10,
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            1440: {
              slidesPerView: 4,
              spaceBetween: 10,
            },
            2560: {
              slidesPerView: 6,
              spaceBetween: 10,
            },
          }}
          modules={[Keyboard, Pagination, Mousewheel]}
          className="mySwiper tw-mt-10"
        >
          {[1, 2, 3, 4].map((_, index) => (
            <SwiperSlide key={index}>
              <SkeletonLoader />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  if (!screenshotImages || screenshotImages.length === 0) {
    return (
      <div>
        <h6 className="tw-mt-8 tw-font-sans tw-text-lg">Display</h6>
        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-text-black tw-bg-white tw-py-8 tw-rounded-xl">
          There are No records
        </div>
      </div>
    );
  }

  return (
    <div className="tw-h-[30rem]">
      <h6 className="tw-mt-8 tw-font-sans tw-text-lg">Display</h6>

      <Swiper
        slidesPerView={4}
        spaceBetween={10}
        mousewheel={true}
        pagination={{
          clickable: true,
          renderBullet: function (_, className) {
            return `<span class="${className} tw-w-3 tw-h-3 tw-bg-gray-300 tw-rounded-full tw-mx-1 tw-cursor-pointer hover:tw-bg-gray-400 active:tw-bg-gray-500"></span>`;
          },
        }}
        keyboard={{
          enabled: true,
        }}
        breakpoints={{
          320: {
            slidesPerView: 1,
            spaceBetween: 10,
          },
          640: {
            slidesPerView: 2,
            spaceBetween: 10,
          },
          768: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
          1440: {
            slidesPerView: 4,
            spaceBetween: 10,
          },
          2560: {
            slidesPerView: 6,
            spaceBetween: 10,
          },
        }}
        modules={[Keyboard, Pagination, Mousewheel]}
        className="mySwiper tw-mt-10"
      >
        {Object.entries(groupedScreenshots).map(
          ([location, screenshots], index) => (
            <React.Fragment key={`${location}-${index}`}>
              {screenshots.map((screenshot, screenshotIndex) => (
                <SwiperSlide
                  key={`${location}-${screenshot.id || screenshotIndex}`}
                  onClick={() => handleSlideClick(screenshot.id)}
                >
                  <div className="p-2 tw-bg-white tw-h-[344px] tw-overflow-y-scroll hide-scroll tw-rounded-xl tw-cursor-pointer">
                    <img
                      src={screenshot.url}
                      alt={""}
                      className="tw-h-52 tw-w-full tw-rounded-md"
                    />

                    <div className="tw-mt-2 tw-px-2 tw-space-y-1">
                      <div className="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-capitalize">
                        <FaDisplay />
                        {screenshot.displayName}
                      </div>

                      <div className="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-capitalize">
                        <FaLocationDot />
                        <p className="!tw-mb-0">
                          {screenshot.location || "Location not assigned yet"}
                        </p>
                      </div>

                      <div className="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-capitalize">
                        <FaPaintBrush />
                        {(!screenshot.layoutNames ||
                          screenshot.layoutNames.length === 0) &&
                          "Layouts not assigned yet"}

                        {screenshot.layoutNames &&
                          Array.isArray(screenshot.layoutNames) &&
                          screenshot.layoutNames.length > 0 && (
                            <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-1">
                              {screenshot.layoutNames.map((layout, idx) => (
                                <span
                                  key={idx}
                                  className="tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-md tw-text-sm"
                                >
                                  {layout}
                                </span>
                              ))}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </React.Fragment>
          )
        )}
      </Swiper>

      <style>
        {`
          .swiper-pagination {
            position: relative;
            margin-top: 20px;
          }
          .swiper-pagination-bullet {
            opacity: 1;
            transition: all 0.3s ease;
            background-color: #ccc;
          }
          .swiper-pagination-bullet-active {
            background-color: #0d6efd !important;
            transform: scale(1.2);
          }
        `}
      </style>
    </div>
  );
};

export default DisplayScreen;

const SkeletonLoader = () => (
  <div className="tw-bg-white tw-h-[344px] tw-rounded-xl tw-p-2">
    <div className="tw-h-52 tw-w-full tw-rounded-md tw-bg-gray-200 animate-pulse" />
    <div className="tw-mt-2 tw-px-2 tw-space-y-3">
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-w-4 tw-h-4 tw-bg-gray-200 animate-pulse tw-rounded" />
        <div className="tw-h-4 tw-bg-gray-200 animate-pulse tw-rounded tw-w-32" />
      </div>
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-w-4 tw-h-4 tw-bg-gray-200 animate-pulse tw-rounded" />
        <div className="tw-h-4 tw-bg-gray-200 animate-pulse tw-rounded tw-w-40" />
      </div>
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-w-4 tw-h-4 tw-bg-gray-200 animate-pulse tw-rounded" />
        <div className="tw-h-4 tw-bg-gray-200 animate-pulse tw-rounded tw-w-36" />
      </div>
      <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-1">
        {[1, 2].map((_, idx) => (
          <div
            key={idx}
            className="tw-h-6 tw-w-20 tw-bg-gray-200 animate-pulse tw-rounded-md"
          />
        ))}
      </div>
    </div>
  </div>
);
